import { Component, inject, InjectionToken, resource } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, Observable, switchMap, of, map } from 'rxjs';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyStatisticModule } from '@vendasta/galaxy/statistic';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { Stat } from './stats';
import { LeadMetricsService } from './lead-metrics.service';
import { MarketsService } from '../../core/markets/markets.service';

const clientLeadsTitle = 'HOME_PAGE.LEAD_METRICS.CLIENT_LEADS.TITLE';
const clientLeadsSubtitle = 'HOME_PAGE.LEAD_METRICS.CLIENT_LEADS.SUBTITLE';
const clientLeadsTooltip = 'HOME_PAGE.LEAD_METRICS.CLIENT_LEADS.TOOLTIP';
const partnerLeadsTitle = 'HOME_PAGE.LEAD_METRICS.PARTNER_LEADS.TITLE';
const partnerLeadsSubtitle = 'HOME_PAGE.LEAD_METRICS.PARTNER_LEADS.SUBTITLE';
const partnerLeadsTooltip = 'HOME_PAGE.LEAD_METRICS.PARTNER_LEADS.TOOLTIP';

interface ResourceData {
  partnerId: string;
  marketId?: string;
}

@Component({
  selector: 'app-lead-metrics',
  imports: [
    GalaxyLoadingSpinnerModule,
    GalaxyStatisticModule,
    GalaxyTooltipModule,
    MatCardModule,
    MatIconModule,
    TranslateModule,
  ],
  templateUrl: './lead-metrics.component.html',
  styleUrl: './lead-metrics.component.scss',
})
export class LeadMetricsComponent {
  private readonly leadsService = inject(LeadMetricsService);
  private readonly marketsService = inject(MarketsService);
  private readonly partnerId = toSignal(
    inject('PARTNER_ID' as unknown as unknown as InjectionToken<Observable<string>>).pipe(distinctUntilChanged()),
  );
  private readonly marketId = toSignal(
    this.marketsService.adminCanAccessAllPartnerMarkets$.pipe(
      switchMap((hasAccessToAllMarkets) => {
        if (hasAccessToAllMarkets) return of('');
        return this.marketsService.getMarkets().pipe(map((markets) => markets[0].market_id));
      }),
    ),
  );
  private readonly request = (): ResourceData => {
    if (!this.partnerId() || this.marketId() === undefined) return undefined;
    if (this.marketId() === '') return { partnerId: this.partnerId() };
    return { partnerId: this.partnerId(), marketId: this.marketId() };
  };

  private readonly clientLeadsResource = resource<Stat, ResourceData>({
    request: this.request,
    loader: async (request) => {
      const { partnerId, marketId } = request.request;
      return this.leadsService.getClientLeads(partnerId, marketId).then(
        ({ total, last30Days }) =>
          ({
            subtitle: clientLeadsSubtitle,
            value: total,
            delta: last30Days,
          }) as Stat,
      );
    },
  });

  private readonly partnerLeadsResource = resource<Stat, ResourceData>({
    request: this.request,
    loader: async (request) => {
      const { partnerId, marketId } = request.request;
      return this.leadsService.getPartnerLeads(partnerId, marketId).then(
        ({ total, last30Days }) =>
          ({
            subtitle: partnerLeadsSubtitle,
            value: total,
            delta: last30Days,
          }) as Stat,
      );
    },
  });

  protected readonly statResources = [
    { title: partnerLeadsTitle, tooltip: partnerLeadsTooltip, resource: this.partnerLeadsResource },
    { title: clientLeadsTitle, tooltip: clientLeadsTooltip, resource: this.clientLeadsResource },
  ];
}
